<!--
AssistantDashboard — Full-only assistant admin dashboard.

Renders the M7a panels (AgentHealthPanel / AgentStatsPanel / AgentAuditPanel),
plus a credit-tiles
section scoped to the currently-selected billing franchise (the M3
CreditBillingSelector value stored in useCreditWallet().selectedFranquiaId).

CREDIT SCOPE: the module-scope selectedFranquiaId ref from useCreditWallet is
the SSOT for the selected franchise (written by CreditBillingSelector, which
already lives in AssistantChat's #billing slot). This component watches that
ref and re-fetches kpis whenever it changes. No new selector is added; no props
are drilled.

LAZY MOUNT: this component is mounted only when the StaffCreditsPage tab is active
(v-if in StaffCreditsPage), so its onMounted fetch fires only when the user
navigates to that page — not on sidebar open.

SECURITY: Full-gate is enforced by the parent route (StaffCreditsPage is
requiresGroups: ['Full']). The four panels enforce their own server-side
guards. This component performs no group check itself — it trusts the parent.
-->
<template>
  <div class="assistant-dashboard" data-testid="assistant-dashboard">
    <!-- Credit tiles for the selected billing franchise -->
    <div class="assistant-dashboard-section">
      <h3 class="section-title">{{ t('Créditos da Franquia') }}</h3>
      <AssistantCreditTiles
        :balance="balance"
        :balance-micro="balanceMicro"
        :kpis="kpis"
        :loading="kpisLoading"
      />
    </div>

    <!-- The M7a panels (reused as-is). AgentContextConfigPanel was removed with
         the agent's /admin/config endpoint: its only control was maxMessages, a
         second source of truth for the context window that M6 retired in favour
         of ai_max_context on ai/agent-settings. -->
    <div class="assistant-config-layout">
      <AgentHealthPanel />
      <AgentStatsPanel />
      <AgentAuditPanel />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AssistantCreditTiles from './AssistantCreditTiles.vue'
import { useCreditWallet } from '../../composables/useCreditWallet'
import { useCreditKpis } from '../../composables/useCreditKpis'
import { log } from '../../utils/DebugLogger'

const { t } = useI18n()

const { selectedFranquiaId, balance, balanceMicro } = useCreditWallet()
const { kpis, loading: kpisLoading, fetchKpis } = useCreditKpis()

async function loadKpis(): Promise<void> {
  if (selectedFranquiaId.value === null) return
  try {
    await fetchKpis(selectedFranquiaId.value)
  } catch {
    log('AssistantDashboard', 'loadKpis', 'Erro ao carregar KPIs de créditos')
  }
}

onMounted(() => {
  void loadKpis()
})

// Re-fetch when the billing franchise selection changes (driven by CreditBillingSelector).
watch(selectedFranquiaId, () => {
  void loadKpis()
})
</script>

<style scoped>
.assistant-dashboard {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.25rem 0;
}

.assistant-dashboard-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-muted-color, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.assistant-config-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
